declare const __CONFIGCAT_SDK_KEY__: string;

import * as configcat from "configcat-js";
import type { IConfigCatClient } from "configcat-js";
export { isMultiHopExecutorEnabled } from "../../shared/featureFlags/multiHopExecutor";
import {
  invalidateOfficeJsBypassFlag,
  isOfficeJsBypassed as readOfficeJsBypassState,
  markOfficeJsUnavailable as markSharedOfficeJsUnavailable,
  registerOfficeJsBypassFlagReader,
  resetOfficeJsBypassFlag,
  setOfficeJsBypassFlag,
} from "../../shared/office/officeJsBypass";
import {
  isBrandingFlagDiagEnabled,
  logBrandingFlagDiag,
  maskSdkKey,
} from "../../shared/featureFlags/brandingFlagDiag";
import {
  invalidateExcelClaudeOpusFlag,
  registerExcelClaudeOpusFlagReader,
  resetExcelClaudeOpusFlag,
  setExcelClaudeOpusFlag,
} from "../../shared/featureFlags/excelExecutorModel";
import {
  invalidateExcelNativeReactFlag,
  registerExcelNativeReactFlagReader,
  resetExcelNativeReactFlag,
  setExcelNativeReactFlag,
} from "../../shared/featureFlags/excelNativeReact";
import {
  getActiveExcelBrandTemplateId,
  resetActiveExcelBrandTemplateId,
  setActiveExcelBrandTemplateId,
} from "../../shared/featureFlags/excelTemplateBrand";
import {
  invalidateAddinsLinksToFactsetWorkstationFlag,
  resetAddinsLinksToFactsetWorkstationFlag,
  setAddinsLinksToFactsetWorkstationFlag,
} from "../../shared/featureFlags/addinsLinksToFactsetWorkstation";
import { publishPickerSkillsState } from "../../shared/featureFlags/pickerSkills";

const FLAG_BYPASS_OFFICE_JS = "bypassOfficeJsApi";
const FLAG_SKILLS = "skills";
const FLAG_PPTX_SKILLS = "pptxSkills";
// Excel rollout flag for user-authored Skills. Deliberately separate from
// `pptxSkills` so each host can be enabled for a different cohort; the shared
// `skills` entitlement still has to be on for either. The remote key is
// all-lowercase and does not follow the `pptxSkills` spelling — match the
// ConfigCat setting exactly, since a key miss silently resolves to `false`.
const FLAG_XLSX_SKILLS = "excelskills";
// Selects the model the Excel executor runs on: off serves GPT-5.5, on serves
// Claude Opus 5. Opus costs roughly 10x per query, so the SDK default stays
// `false` and every unresolved state reads as off. The resolved value is
// published to `shared/featureFlags/excelExecutorModel`, which Excel reads.
const FLAG_EXCEL_USE_CLAUDE_OPUS = "excelUseClaudeOpus";
// Selects the experimental native ReAct executor for new Excel runs. This is
// independent of model selection and fails closed to the production runner.
const FLAG_EXCEL_NATIVE_REACT = "excelNativeReact";
// Outbound add-in links into FactSet Workstation (e.g. Manage Skills). Off
// until ConfigCat answers; combined in the UI with the host Skills gates.
const FLAG_ADDINS_LINKS_TO_FACTSET_WORKSTATION = "enableAddinsLinksToFactsetWorkstation";
// Fail-closed ConfigCat switch for PowerPoint ActiveGraphs. The add-in
// evaluates it with `isPowerPointActiveGraphsEnabled()` — same contract as
// Skills. Unresolved / no-SDK states stay off. The ConfigCat key is
// all-lowercase; a casing mismatch silently resolves to `false`.
const FLAG_PPTX_ACTIVE_GRAPHS = "pptxactivegraphs";
// Single ConfigCat text setting whose served value is a brand id (see
// `brandRegistry.ts`). Replaces the old one-boolean-flag-per-brand scheme
// (showPptxCitiBranding, showPptxCavendishBranding, ...): add a new client by
// appending a `BrandConfig` to the registry plus a new variation on this ONE
// flag, instead of creating a new ConfigCat flag every time.
const FLAG_PPTX_TEMPLATE_BRAND = "pptxTemplateBrand";
const DEFAULT_BRAND_TEMPLATE_ID = "";
const FLAG_EXCEL_TEMPLATE_BRAND = "excelTemplateBrand";
const DEFAULT_EXCEL_BRAND_TEMPLATE_ID = "";
const CONFIGCAT_POLL_INTERVAL_SECONDS = 120;
const UI_REFRESH_COOLDOWN_MS = 30_000;

export type FeatureFlagsInitStatus = "idle" | "initializing" | "ready" | "disabled";

interface FeatureFlagRequestContext {
  readonly client: IConfigCatClient;
  readonly generation: number;
  readonly accountScopeKey: string;
  readonly userEmail: string;
  readonly user: configcat.User | undefined;
}

let client: IConfigCatClient | null = null;
let currentUserEmail = "";
let currentAccountScopeKey = "";
let runtimeSdkKeyOverride = "";
let activeSdkKey = "";
let featureFlagsInitStatus: FeatureFlagsInitStatus = "idle";
let refreshAllPromise: Promise<void> | null = null;
let refreshAllGeneration: number | null = null;
let refreshAllIncludesRemoteFetch = false;
let featureFlagGeneration = 0;
let lastSuccessfulRemoteRefreshAt = 0;
let cachedBypassValue: boolean | null = null;
let cachedSkillsValue: boolean | null = null;
let cachedPptxSkillsValue: boolean | null = null;
let cachedXlsxSkillsValue: boolean | null = null;
let cachedExcelClaudeOpusValue: boolean | null = null;
let cachedExcelNativeReactValue: boolean | null = null;
let cachedAddinsLinksToFactsetWorkstationValue: boolean | null = null;
let cachedPptxActiveGraphsValue: boolean | null = null;
// Active brand-template id cache (null = not yet resolved). Mirrors the
// lazy-refresh contract of the other flags.
let cachedBrandTemplateId: string | null = null;
const loggedBrandingFlagWarnings = new Set<string>();

function setFeatureFlagsInitStatus(status: FeatureFlagsInitStatus): void {
  if (featureFlagsInitStatus === status) return;
  featureFlagsInitStatus = status;
  logBrandingFlagDiag("initStatusChanged", {
    status,
    userEmail: currentUserEmail || null,
  });
  notifyFeatureFlagListeners();
}

function logBrandingFlagSnapshot(reason: string): void {
  if (!isBrandingFlagDiagEnabled()) return;
  logBrandingFlagDiag("clientSnapshot", {
    reason,
    userEmail: currentUserEmail || null,
    initStatus: featureFlagsInitStatus,
    configCatClientActive: Boolean(client),
    bypassOfficeJsApi: cachedBypassValue,
    skills: cachedSkillsValue,
    pptxSkills: cachedPptxSkillsValue,
    xlsxSkills: cachedXlsxSkillsValue,
    powerPointSkillsEnabled: cachedSkillsValue === true && cachedPptxSkillsValue === true,
    excelSkillsEnabled: cachedSkillsValue === true && cachedXlsxSkillsValue === true,
    excelUseClaudeOpus: cachedExcelClaudeOpusValue,
    excelNativeReact: cachedExcelNativeReactValue,
    enableAddinsLinksToFactsetWorkstation: cachedAddinsLinksToFactsetWorkstationValue,
    [FLAG_PPTX_ACTIVE_GRAPHS]: cachedPptxActiveGraphsValue,
    activeBrandTemplateId: cachedBrandTemplateId,
    activeExcelBrandTemplateId: getActiveExcelBrandTemplateId(),
  });
}

function clearBrandTemplateCache(): void {
  cachedBrandTemplateId = null;
}
clearBrandTemplateCache();

function resetAllFlagCaches(): void {
  cachedBypassValue = null;
  resetOfficeJsBypassFlag();
  invalidateSkillsFlagCache();
  // Init and logout both drop the Excel executor back to GPT-5.5 rather than
  // carrying the previous session's Opus entitlement into the next one.
  cachedExcelClaudeOpusValue = null;
  resetExcelClaudeOpusFlag();
  cachedExcelNativeReactValue = null;
  resetExcelNativeReactFlag();
  cachedAddinsLinksToFactsetWorkstationValue = null;
  resetAddinsLinksToFactsetWorkstationFlag();
  cachedPptxActiveGraphsValue = null;
  clearBrandTemplateCache();
  resetActiveExcelBrandTemplateId();
}

function invalidateBypassFlagCache(): void {
  cachedBypassValue = null;
  invalidateOfficeJsBypassFlag();
}

function invalidateExcelClaudeOpusFlagCache(): void {
  cachedExcelClaudeOpusValue = null;
  invalidateExcelClaudeOpusFlag();
}

/**
 * Invalidate the service and shared native-executor caches together.
 *
 * @returns Nothing.
 */
function invalidateExcelNativeReactFlagCache(): void {
  cachedExcelNativeReactValue = null;
  invalidateExcelNativeReactFlag();
}

function invalidateAddinsLinksToFactsetWorkstationFlagCache(): void {
  cachedAddinsLinksToFactsetWorkstationValue = null;
  invalidateAddinsLinksToFactsetWorkstationFlag();
}

function invalidateActiveGraphFlagCache(): void {
  cachedPptxActiveGraphsValue = null;
}

function invalidateSkillsFlagCache(): void {
  cachedSkillsValue = null;
  cachedPptxSkillsValue = null;
  cachedXlsxSkillsValue = null;
  publishSkillsGateState();
}

/**
 * Mirrors the resolved Skills gate into `shared/featureFlags/pickerSkills`,
 * which is how Excel and shared UI read it without importing this workspace.
 * Idempotent, so it is safe to call from every transition.
 */
function publishSkillsGateState(): void {
  publishPickerSkillsState({
    ready: Boolean(client) && featureFlagsInitStatus === "ready",
    skills: cachedSkillsValue,
    pptxSkills: cachedPptxSkillsValue,
    xlsxSkills: cachedXlsxSkillsValue,
    contextGeneration: featureFlagGeneration,
  });
}

registerOfficeJsBypassFlagReader(readFeatureFlagOfficeJsBypass);
registerExcelClaudeOpusFlagReader(readFeatureFlagExcelClaudeOpus);
registerExcelNativeReactFlagReader(readFeatureFlagExcelNativeReact);

type FeatureFlagListener = () => void;
const listeners = new Set<FeatureFlagListener>();

function notifyFeatureFlagListeners(): void {
  publishSkillsGateState();
  listeners.forEach((listener) => {
    try {
      listener();
    } catch (err) {
      console.warn("[FeatureFlags] Listener threw", err);
    }
  });
}

/**
 * Subscribe to feature-flag cache changes. Listeners fire when the
 * ConfigCat client reports a config change, when a lazy refresh
 * resolves to a different value, or when the client is disposed
 * (e.g. logout).
 *
 * Used by the `useActiveBrand` hook (via `useSyncExternalStore`)
 * so React components re-render when remote flags flip mid-session.
 */
export function subscribeFeatureFlags(listener: FeatureFlagListener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function getFeatureFlagsInitStatus(): FeatureFlagsInitStatus {
  return featureFlagsInitStatus;
}

export function areBrandingFeatureFlagsReady(): boolean {
  return featureFlagsInitStatus === "ready";
}

export async function refreshFeatureFlagsForUi(reason: string = "uiRefresh"): Promise<void> {
  if (!client || !currentUserEmail || featureFlagsInitStatus === "disabled") {
    logBrandingFlagDiag("uiRefreshSkipped", {
      reason,
      userEmail: currentUserEmail || null,
      initStatus: featureFlagsInitStatus,
      clientActive: Boolean(client),
    });
    return;
  }

  if (refreshAllPromise && refreshAllIncludesRemoteFetch) {
    logBrandingFlagDiag("uiRefreshJoined", {
      reason,
      userEmail: currentUserEmail,
    });
    return refreshAllPromise;
  }

  const now = Date.now();
  if (
    lastSuccessfulRemoteRefreshAt &&
    now - lastSuccessfulRemoteRefreshAt < UI_REFRESH_COOLDOWN_MS
  ) {
    logBrandingFlagDiag("uiRefreshThrottled", {
      reason,
      userEmail: currentUserEmail,
      msSinceLastRefresh: now - lastSuccessfulRemoteRefreshAt,
      cooldownMs: UI_REFRESH_COOLDOWN_MS,
    });
    return;
  }

  await refreshAllFeatureFlags(reason, featureFlagGeneration, { forceRemote: true });
}

function getConfigCatSdkKey(): string {
  // Prefer the rspack-injected build-time key (per-env GitHub secret at CI).
  // Fall back to the session key when the build key is absent (e.g. local without
  // CONFIGCAT_SDK_KEY, or older bundles).
  const buildKey = typeof __CONFIGCAT_SDK_KEY__ !== "undefined" ? __CONFIGCAT_SDK_KEY__ : "";
  return buildKey || runtimeSdkKeyOverride;
}

function createConfigCatClient(
  sdkKey: string,
  generation: number,
  accountScopeKey: string
): IConfigCatClient {
  let createdClient: IConfigCatClient | null = null;
  createdClient = configcat.getClient(sdkKey, configcat.PollingMode.AutoPoll, {
    pollIntervalSeconds: CONFIGCAT_POLL_INTERVAL_SECONDS,
    logger: configcat.createConsoleLogger(configcat.LogLevel.Warn),
    setupHooks: (hooks) => {
      hooks.on("configChanged", () => {
        if (!createdClient) return;
        handleConfigChanged(createdClient, generation, accountScopeKey);
      });
    },
  });
  return createdClient;
}

function handleConfigChanged(
  requestClient: IConfigCatClient,
  requestGeneration: number,
  requestAccountScopeKey: string
): void {
  if (
    !isFeatureFlagContextCurrent(
      requestClient,
      requestGeneration,
      requestAccountScopeKey
    )
  ) {
    return;
  }

  const wasReady = featureFlagsInitStatus === "ready";
  invalidateBypassFlagCache();
  invalidateExcelClaudeOpusFlagCache();
  invalidateExcelNativeReactFlagCache();
  if (featureFlagsInitStatus !== "ready") {
    // A config push is not a value change. Blanking the UI-facing caches on a
    // ready session pulls the Skills entries out of the composer until the
    // refresh below resolves, even when these flags did not move, so the last
    // resolved values stay published and the refresh notifies on a real flip.
    invalidateSkillsFlagCache();
    invalidateActiveGraphFlagCache();
    invalidateAddinsLinksToFactsetWorkstationFlagCache();
    clearBrandTemplateCache();
    setFeatureFlagsInitStatus("initializing");
  }
  if (wasReady) notifyFeatureFlagListeners();
  console.log("[FeatureFlags] Config changed — cache invalidated");
  logBrandingFlagDiag("configChanged", { userEmail: currentUserEmail });
  refreshAllFeatureFlags("configChanged", requestGeneration).catch((error) => {
    if (
      !isFeatureFlagContextCurrent(
        requestClient,
        requestGeneration,
        requestAccountScopeKey
      )
    ) {
      return;
    }
    logBrandingFlagDiag("configChangedRefreshFailed", {
      userEmail: currentUserEmail,
      error: error instanceof Error ? error.message : String(error),
    });
  });
}

async function forceRefreshRemoteConfig(
  reason: string,
  request: FeatureFlagRequestContext
): Promise<boolean> {
  if (!isFeatureFlagRequestCurrent(request)) return false;

  logBrandingFlagDiag("remoteRefreshStart", {
    reason,
    userEmail: request.userEmail || null,
  });

  let result: Awaited<ReturnType<IConfigCatClient["forceRefreshAsync"]>>;
  try {
    result = await request.client.forceRefreshAsync();
  } catch (error) {
    if (!isFeatureFlagRequestCurrent(request)) return false;
    throw error;
  }

  if (!isFeatureFlagRequestCurrent(request)) return false;

  if (!result.isSuccess) {
    const errorMessage = result.errorMessage || "ConfigCat force refresh failed";
    logBrandingFlagDiag("remoteRefreshFailed", {
      reason,
      userEmail: request.userEmail || null,
      error: errorMessage,
    });
    throw new Error(errorMessage);
  }

  logBrandingFlagDiag("remoteRefreshSucceeded", {
    reason,
    userEmail: request.userEmail || null,
  });
  return true;
}

/**
 * Call once at startup when the Office JS runtime cannot be loaded at all.
 * After this, `isOfficeJsBypassed()` returns `true` unconditionally.
 */
export function markOfficeJsUnavailable(): void {
  markSharedOfficeJsUnavailable();
  console.log("[FeatureFlags] Office JS marked as unavailable — all Office calls will be NOPs");
}

export async function initFeatureFlags(
  userEmail: string,
  options?: {
    sdkKey?: string;
    accountScopeKey?: string | null;
    companyId?: string | null;
  }
): Promise<void> {
  if (options?.sdkKey?.trim()) {
    runtimeSdkKeyOverride = options.sdkKey.trim();
  }

  const sdkKey = getConfigCatSdkKey();
  const sdkKeyChanged = client !== null && activeSdkKey !== sdkKey;
  const companyId = options?.companyId?.trim() || "";
  const accountScopeKey =
    options?.accountScopeKey?.trim() ||
    JSON.stringify([userEmail.trim(), companyId || null]);

  if (
    currentUserEmail === userEmail &&
    currentAccountScopeKey === accountScopeKey &&
    client &&
    !sdkKeyChanged &&
    (featureFlagsInitStatus === "ready" ||
      (featureFlagsInitStatus === "initializing" && refreshAllPromise))
  ) {
    logBrandingFlagDiag("initSkipped", {
      userEmail,
      companyId: companyId || null,
      reason: "alreadyInitialized",
    });
    return;
  }

  logBrandingFlagDiag("initStart", {
    userEmail,
    companyId: companyId || null,
    sdkKey: maskSdkKey(sdkKey),
    sdkKeySource:
      typeof __CONFIGCAT_SDK_KEY__ !== "undefined" && __CONFIGCAT_SDK_KEY__
        ? "buildTime"
        : runtimeSdkKeyOverride
          ? "session"
          : "none",
    brandFlagKey: FLAG_PPTX_TEMPLATE_BRAND,
  });

  currentUserEmail = userEmail;
  currentAccountScopeKey = accountScopeKey;
  featureFlagGeneration += 1;
  const initGeneration = featureFlagGeneration;
  refreshAllPromise = null;
  refreshAllGeneration = null;
  refreshAllIncludesRemoteFetch = false;
  lastSuccessfulRemoteRefreshAt = 0;
  setFeatureFlagsInitStatus("initializing");
  resetAllFlagCaches();

  if (!sdkKey) {
    console.warn("[FeatureFlags] No ConfigCat SDK key configured — flags disabled");
    activeSdkKey = "";
    setFeatureFlagsInitStatus("disabled");
    logBrandingFlagDiag("initAborted", {
      userEmail,
      reason: "missingSdkKey",
      hint: "Set CONFIGCAT_SDK_KEY in FinsterAddIn/.env and restart the dev server, or ensure /api/auth/session returns features.configcatSdkKey.",
    });
    return;
  }

  if (client) {
    client.dispose();
    client = null;
  }

  client = createConfigCatClient(sdkKey, initGeneration, accountScopeKey);
  activeSdkKey = sdkKey;

  logBrandingFlagDiag("clientCreated", {
    userEmail,
    pollIntervalSeconds: CONFIGCAT_POLL_INTERVAL_SECONDS,
  });

  try {
    await refreshAllFeatureFlags("initComplete", initGeneration, { forceRemote: true });
  } catch (error) {
    logBrandingFlagDiag("initFetchFailed", {
      userEmail,
      error: error instanceof Error ? error.message : String(error),
    });
    try {
      await new Promise((r) => setTimeout(r, 1000));
      await refreshAllFeatureFlags("initCompleteAfterRetry", initGeneration, { forceRemote: true });
    } catch (retryError) {
      console.warn(
        "[FeatureFlags] Initial remote flag refresh failed after retry — branding UI remains hidden until auto-poll succeeds"
      );
      logBrandingFlagDiag("initFetchFailedAfterRetry", {
        userEmail,
        error: retryError instanceof Error ? retryError.message : String(retryError),
      });
    }
  }
}

async function refreshAllFeatureFlags(
  reason: string,
  generation: number,
  options: { forceRemote?: boolean } = {}
): Promise<void> {
  const forceRemote = options.forceRemote === true;
  const request = captureFeatureFlagRequest();
  if (!request || request.generation !== generation) {
    logBrandingFlagDiag("refreshSkipped", {
      reason,
      generation,
      currentGeneration: featureFlagGeneration,
      clientActive: Boolean(client),
    });
    return;
  }

  if (refreshAllPromise && refreshAllGeneration === generation) {
    if (!forceRemote || refreshAllIncludesRemoteFetch) {
      return refreshAllPromise;
    }

    await refreshAllPromise;
    if (!isFeatureFlagRequestCurrent(request)) {
      logBrandingFlagDiag("refreshSkippedAfterJoinedRefresh", {
        reason,
        generation,
        currentGeneration: featureFlagGeneration,
        clientActive: Boolean(client),
      });
      return;
    }
  }

  const refreshPromise = (async () => {
    if (forceRemote) {
      const refreshed = await forceRefreshRemoteConfig(reason, request);
      if (!refreshed) return;
    }

    if (!isFeatureFlagRequestCurrent(request)) return;

    await Promise.all([
      refreshBypassFlag(request),
      refreshSkillsFlags(request),
      refreshBrandTemplateId(request),
      refreshExcelBrandTemplateId(request),
      refreshExcelClaudeOpusFlag(request),
      refreshExcelNativeReactFlag(request),
      refreshAddinsLinksToFactsetWorkstationFlag(request),
      refreshActiveGraphFlags(request),
    ]);
    if (!isFeatureFlagRequestCurrent(request)) {
      logBrandingFlagDiag("refreshIgnored", {
        reason,
        generation,
        currentGeneration: featureFlagGeneration,
        clientActive: Boolean(client),
      });
      return;
    }
    if (forceRemote) {
      lastSuccessfulRemoteRefreshAt = Date.now();
    }
    setFeatureFlagsInitStatus("ready");
    logBrandingFlagSnapshot(reason);
  })();
  refreshAllPromise = refreshPromise;
  refreshAllGeneration = generation;
  refreshAllIncludesRemoteFetch = forceRemote;

  try {
    await refreshPromise;
  } finally {
    if (refreshAllPromise === refreshPromise) {
      refreshAllPromise = null;
      refreshAllGeneration = null;
      refreshAllIncludesRemoteFetch = false;
    }
  }
}

export function disposeFeatureFlags(): void {
  if (client) {
    client.dispose();
    client = null;
  }
  resetAllFlagCaches();
  loggedBrandingFlagWarnings.clear();
  activeSdkKey = "";
  refreshAllPromise = null;
  refreshAllGeneration = null;
  refreshAllIncludesRemoteFetch = false;
  lastSuccessfulRemoteRefreshAt = 0;
  featureFlagGeneration += 1;
  logBrandingFlagDiag("disposed", { userEmail: currentUserEmail || null });
  currentUserEmail = "";
  currentAccountScopeKey = "";
  runtimeSdkKeyOverride = "";
  featureFlagsInitStatus = "idle";
  notifyFeatureFlagListeners();
}

function buildUser(): configcat.User | undefined {
  if (!currentUserEmail) return undefined;
  return new configcat.User(currentUserEmail, currentUserEmail);
}

function captureFeatureFlagRequest(): FeatureFlagRequestContext | null {
  const requestClient = client;
  if (!requestClient) return null;

  return {
    client: requestClient,
    generation: featureFlagGeneration,
    accountScopeKey: currentAccountScopeKey,
    userEmail: currentUserEmail,
    user: buildUser(),
  };
}

function isFeatureFlagContextCurrent(
  requestClient: IConfigCatClient,
  requestGeneration: number,
  requestAccountScopeKey: string
): boolean {
  return (
    requestClient === client &&
    requestGeneration === featureFlagGeneration &&
    requestAccountScopeKey === currentAccountScopeKey
  );
}

function isFeatureFlagRequestCurrent(request: FeatureFlagRequestContext): boolean {
  return isFeatureFlagContextCurrent(
    request.client,
    request.generation,
    request.accountScopeKey
  );
}

async function readFeatureFlagValue<T extends boolean | string>(
  request: FeatureFlagRequestContext,
  flagKey: string,
  defaultValue: T
): Promise<T | null> {
  if (!isFeatureFlagRequestCurrent(request)) return null;

  try {
    const value = (await request.client.getValueAsync(
      flagKey,
      defaultValue,
      request.user
    )) as T;
    return isFeatureFlagRequestCurrent(request) ? value : null;
  } catch (error) {
    if (!isFeatureFlagRequestCurrent(request)) return null;
    throw error;
  }
}

async function refreshBypassFlag(
  request: FeatureFlagRequestContext | null = captureFeatureFlagRequest()
): Promise<boolean> {
  if (!request) {
    cachedBypassValue = false;
    setOfficeJsBypassFlag(false);
    return false;
  }

  const value = await readFeatureFlagValue(request, FLAG_BYPASS_OFFICE_JS, false);
  if (value === null || !isFeatureFlagRequestCurrent(request)) return false;

  const changed = cachedBypassValue !== value;
  cachedBypassValue = value;
  setOfficeJsBypassFlag(value);
  console.log(`[FeatureFlags] ${FLAG_BYPASS_OFFICE_JS} = ${value} (user: ${currentUserEmail})`);
  if (changed) notifyFeatureFlagListeners();
  return value;
}

async function refreshExcelClaudeOpusFlag(
  request: FeatureFlagRequestContext | null = captureFeatureFlagRequest()
): Promise<boolean> {
  if (!request) {
    cachedExcelClaudeOpusValue = false;
    setExcelClaudeOpusFlag(false);
    return false;
  }

  const value = await readFeatureFlagValue(request, FLAG_EXCEL_USE_CLAUDE_OPUS, false);
  if (value === null || !isFeatureFlagRequestCurrent(request)) return false;

  const changed = cachedExcelClaudeOpusValue !== value;
  cachedExcelClaudeOpusValue = value;
  setExcelClaudeOpusFlag(value);
  console.log(
    `[FeatureFlags] ${FLAG_EXCEL_USE_CLAUDE_OPUS} = ${value} (user: ${currentUserEmail})`
  );
  if (changed) notifyFeatureFlagListeners();
  return value;
}

/**
 * Resolve and publish the native-executor gate for the captured user context.
 *
 * Missing context, unresolved values, and stale async responses all fail
 * closed. Only a current explicit `true` can activate native mode.
 *
 * @param request - Immutable account-scoped flag request, or `null` when the
 * feature service is unavailable.
 * @returns Whether native mode is explicitly enabled for the current request.
 */
async function refreshExcelNativeReactFlag(
  request: FeatureFlagRequestContext | null = captureFeatureFlagRequest(),
): Promise<boolean> {
  if (!request) {
    cachedExcelNativeReactValue = false;
    setExcelNativeReactFlag(false);
    return false;
  }

  const value = await readFeatureFlagValue(
    request,
    FLAG_EXCEL_NATIVE_REACT,
    false,
  );
  if (value === null || !isFeatureFlagRequestCurrent(request)) return false;

  const changed = cachedExcelNativeReactValue !== value;
  cachedExcelNativeReactValue = value;
  setExcelNativeReactFlag(value);
  console.log(
    `[FeatureFlags] ${FLAG_EXCEL_NATIVE_REACT} = ${value} (user: ${currentUserEmail})`,
  );
  if (changed) notifyFeatureFlagListeners();
  return value;
}

async function refreshAddinsLinksToFactsetWorkstationFlag(
  request: FeatureFlagRequestContext | null = captureFeatureFlagRequest()
): Promise<boolean> {
  if (!request) {
    cachedAddinsLinksToFactsetWorkstationValue = false;
    setAddinsLinksToFactsetWorkstationFlag(false);
    return false;
  }

  const value = await readFeatureFlagValue(
    request,
    FLAG_ADDINS_LINKS_TO_FACTSET_WORKSTATION,
    false
  );
  if (value === null || !isFeatureFlagRequestCurrent(request)) return false;

  const changed = cachedAddinsLinksToFactsetWorkstationValue !== value;
  cachedAddinsLinksToFactsetWorkstationValue = value;
  setAddinsLinksToFactsetWorkstationFlag(value);
  console.log(
    `[FeatureFlags] ${FLAG_ADDINS_LINKS_TO_FACTSET_WORKSTATION} = ${value} (user: ${currentUserEmail})`
  );
  if (changed) notifyFeatureFlagListeners();
  return value;
}

async function refreshActiveGraphFlags(
  request: FeatureFlagRequestContext | null = captureFeatureFlagRequest()
): Promise<boolean> {
  if (!request) {
    cachedPptxActiveGraphsValue = false;
    return false;
  }
  const enabled = await readFeatureFlagValue(request, FLAG_PPTX_ACTIVE_GRAPHS, false);
  if (enabled === null || !isFeatureFlagRequestCurrent(request)) return false;

  const changed = cachedPptxActiveGraphsValue !== enabled;
  cachedPptxActiveGraphsValue = enabled;
  console.log(
    `[FeatureFlags] ${FLAG_PPTX_ACTIVE_GRAPHS} = ${enabled} (user: ${currentUserEmail})`
  );
  if (changed) notifyFeatureFlagListeners();
  return enabled;
}

/**
 * Resolves the shared entitlement and both host rollout flags together. They
 * are read in one `Promise.all` and committed in one step so neither host's
 * gate can observe a half-applied combination.
 */
async function refreshSkillsFlags(
  request: FeatureFlagRequestContext | null = captureFeatureFlagRequest()
): Promise<boolean> {
  if (!request) {
    const changed =
      cachedSkillsValue !== false ||
      cachedPptxSkillsValue !== false ||
      cachedXlsxSkillsValue !== false;
    cachedSkillsValue = false;
    cachedPptxSkillsValue = false;
    cachedXlsxSkillsValue = false;
    if (changed) notifyFeatureFlagListeners();
    return false;
  }

  const [skillsEnabled, pptxSkillsEnabled, xlsxSkillsEnabled] = await Promise.all([
    readFeatureFlagValue(request, FLAG_SKILLS, false),
    readFeatureFlagValue(request, FLAG_PPTX_SKILLS, false),
    readFeatureFlagValue(request, FLAG_XLSX_SKILLS, false),
  ]);

  if (
    skillsEnabled === null ||
    pptxSkillsEnabled === null ||
    xlsxSkillsEnabled === null ||
    !isFeatureFlagRequestCurrent(request)
  ) {
    return false;
  }

  const changed =
    cachedSkillsValue !== skillsEnabled ||
    cachedPptxSkillsValue !== pptxSkillsEnabled ||
    cachedXlsxSkillsValue !== xlsxSkillsEnabled;
  cachedSkillsValue = skillsEnabled;
  cachedPptxSkillsValue = pptxSkillsEnabled;
  cachedXlsxSkillsValue = xlsxSkillsEnabled;
  console.log(
    `[FeatureFlags] ${FLAG_SKILLS} = ${skillsEnabled}, ${FLAG_PPTX_SKILLS} = ${pptxSkillsEnabled}, ${FLAG_XLSX_SKILLS} = ${xlsxSkillsEnabled} (user: ${currentUserEmail})`
  );
  if (changed) notifyFeatureFlagListeners();
  return skillsEnabled && pptxSkillsEnabled;
}

async function refreshBrandTemplateId(
  request: FeatureFlagRequestContext | null = captureFeatureFlagRequest()
): Promise<string> {
  if (!request) {
    cachedBrandTemplateId = DEFAULT_BRAND_TEMPLATE_ID;
    return DEFAULT_BRAND_TEMPLATE_ID;
  }

  const value = await readFeatureFlagValue(
    request,
    FLAG_PPTX_TEMPLATE_BRAND,
    DEFAULT_BRAND_TEMPLATE_ID
  );
  if (value === null || !isFeatureFlagRequestCurrent(request)) {
    return DEFAULT_BRAND_TEMPLATE_ID;
  }

  const changed = cachedBrandTemplateId !== value;
  cachedBrandTemplateId = value;
  console.log(`[FeatureFlags] ${FLAG_PPTX_TEMPLATE_BRAND} = ${value} (user: ${currentUserEmail})`);
  logBrandingFlagDiag("brandingFlagRefreshed", {
    flagKey: FLAG_PPTX_TEMPLATE_BRAND,
    brandId: value || null,
    value,
    userEmail: currentUserEmail,
    changed,
  });
  if (changed) notifyFeatureFlagListeners();
  return value;
}

async function refreshExcelBrandTemplateId(
  request: FeatureFlagRequestContext | null = captureFeatureFlagRequest()
): Promise<string> {
  if (!request) {
    return getActiveExcelBrandTemplateId();
  }

  try {
    const value = await readFeatureFlagValue(
      request,
      FLAG_EXCEL_TEMPLATE_BRAND,
      DEFAULT_EXCEL_BRAND_TEMPLATE_ID
    );

    if (value === null || !isFeatureFlagRequestCurrent(request)) {
      return getActiveExcelBrandTemplateId();
    }

    setActiveExcelBrandTemplateId(value);
    const activeValue = getActiveExcelBrandTemplateId();
    console.log(
      `[FeatureFlags] ${FLAG_EXCEL_TEMPLATE_BRAND} = ${activeValue} (user: ${currentUserEmail})`
    );
    return activeValue;
  } catch (error) {
    if (!isFeatureFlagRequestCurrent(request)) {
      return getActiveExcelBrandTemplateId();
    }

    const activeValue = getActiveExcelBrandTemplateId();
    console.warn(
      `[FeatureFlags] Failed to refresh ${FLAG_EXCEL_TEMPLATE_BRAND}; keeping ${activeValue || "(empty)"}`,
      error
    );
    logBrandingFlagDiag("excelBrandingFlagRefreshFailed", {
      flagKey: FLAG_EXCEL_TEMPLATE_BRAND,
      brandId: activeValue || null,
      userEmail: currentUserEmail,
      error: error instanceof Error ? error.message : String(error),
    });
    return activeValue;
  }
}

/**
 * Synchronous check for whether Office JS calls should be bypassed.
 * Returns `true` when:
 *   1. Office JS runtime is not loaded at all, OR
 *   2. The ConfigCat `bypassOfficeJsApi` flag is enabled for the current user.
 * Safe to call on every Office JS entry point — no await required.
 */
export function isOfficeJsBypassed(): boolean {
  return readOfficeJsBypassState();
}

/**
 * Synchronous add-in gate for PowerPoint Skills. Both the shared `skills`
 * entitlement and the PowerPoint rollout flag must be resolved and enabled
 * for the current account. Unresolved, disabled, and no-SDK states fail closed.
 */
export function isPowerPointSkillsEnabled(): boolean {
  if (!client || featureFlagsInitStatus !== "ready") return false;
  return cachedSkillsValue === true && cachedPptxSkillsValue === true;
}

export interface PowerPointSkillsGateSnapshot {
  readonly enabled: boolean;
  /** Changes on account/session reinitialization, but not a same-account flag refresh. */
  readonly contextGeneration: number;
}

/** Atomically capture the complete PowerPoint Skills decision for one submission. */
export function snapshotPowerPointSkillsGate(): PowerPointSkillsGateSnapshot {
  return Object.freeze({
    enabled: isPowerPointSkillsEnabled(),
    contextGeneration: featureFlagGeneration,
  });
}

export function isPowerPointSkillsGateSnapshotCurrent(
  snapshot: PowerPointSkillsGateSnapshot
): boolean {
  return snapshot.contextGeneration === featureFlagGeneration;
}

/**
 * Synchronous add-in gate for PowerPoint ActiveGraphs. Unresolved, disabled,
 * and no-SDK states fail closed — same contract as `isPowerPointSkillsEnabled`.
 */
export function isPowerPointActiveGraphsEnabled(): boolean {
  if (!client || featureFlagsInitStatus !== "ready") return false;
  return cachedPptxActiveGraphsValue === true;
}

export interface PowerPointActiveGraphGateSnapshot {
  readonly enabled: boolean;
  readonly contextGeneration: number;
}

export function snapshotPowerPointActiveGraphGate(): PowerPointActiveGraphGateSnapshot {
  return Object.freeze({
    enabled: isPowerPointActiveGraphsEnabled(),
    contextGeneration: featureFlagGeneration,
  });
}

export function isPowerPointActiveGraphGateSnapshotCurrent(
  snapshot: PowerPointActiveGraphGateSnapshot
): boolean {
  return snapshot.contextGeneration === featureFlagGeneration;
}

function readFeatureFlagOfficeJsBypass(): boolean {
  if (!client) return false;

  if (cachedBypassValue !== null) {
    return cachedBypassValue;
  }

  if (refreshAllPromise) {
    return false;
  }

  refreshBypassFlag().catch(() => {});
  return false;
}

function readFeatureFlagExcelClaudeOpus(): boolean {
  if (!client) return false;

  if (cachedExcelClaudeOpusValue !== null) {
    return cachedExcelClaudeOpusValue;
  }

  if (refreshAllPromise) {
    return false;
  }

  refreshExcelClaudeOpusFlag().catch(() => {});
  return false;
}

/**
 * Read the native-executor flag synchronously for Excel run admission.
 *
 * Cached values are returned immediately. An unresolved cache schedules a
 * background refresh and returns `false`, preserving fail-closed semantics.
 *
 * @returns The cached explicit enablement state; otherwise `false`.
 */
function readFeatureFlagExcelNativeReact(): boolean {
  if (!client) return false;

  if (cachedExcelNativeReactValue !== null) {
    return cachedExcelNativeReactValue;
  }

  if (refreshAllPromise) {
    return false;
  }

  refreshExcelNativeReactFlag().catch(() => {});
  return false;
}

/**
 * Synchronous read of the active brand-template id for the current user, as
 * served by the single `pptxTemplateBrand` ConfigCat text setting. Returns
 * `""` (no brand) until ConfigCat resolves the flag for the current user.
 *
 * Safe to call from a render path — pair with `subscribeFeatureFlags`
 * (e.g. via the `useActiveBrand` hook) so components re-render when the flag
 * flips mid-session.
 */
export function getActiveBrandTemplateId(): string {
  const flagKey = FLAG_PPTX_TEMPLATE_BRAND;
  if (!client) {
    const warningKey =
      featureFlagsInitStatus === "disabled"
        ? "disabled"
        : featureFlagsInitStatus === "idle"
          ? "notStarted"
          : featureFlagsInitStatus === "initializing"
            ? "initializing"
            : "noClient";
    if (!loggedBrandingFlagWarnings.has(warningKey)) {
      loggedBrandingFlagWarnings.add(warningKey);
      logBrandingFlagDiag("brandingFlagCheck", {
        flagKey,
        result: DEFAULT_BRAND_TEMPLATE_ID,
        reason:
          featureFlagsInitStatus === "disabled"
            ? "configCatClientDisabled"
            : featureFlagsInitStatus === "idle"
              ? "initNotStartedYet"
              : featureFlagsInitStatus === "initializing"
                ? "initInProgress"
                : "configCatClientNotInitialized",
        initStatus: featureFlagsInitStatus,
        hint:
          featureFlagsInitStatus === "disabled"
            ? "initAborted/missingSdkKey — check initStart log for sdkKeySource."
            : featureFlagsInitStatus === "idle"
              ? "UI rendered before initFeatureFlags() useEffect; should resolve after login init."
              : featureFlagsInitStatus === "initializing"
                ? "ConfigCat initialization is still resolving; branding UI remains hidden until ready."
                : "initFeatureFlags() ran but client is missing; check initAborted logs.",
      });
    }
    return DEFAULT_BRAND_TEMPLATE_ID;
  }

  if (cachedBrandTemplateId !== null) {
    return cachedBrandTemplateId;
  }

  if (refreshAllPromise) {
    return DEFAULT_BRAND_TEMPLATE_ID;
  }

  const pendingKey = `pending:${flagKey}`;
  if (!loggedBrandingFlagWarnings.has(pendingKey)) {
    loggedBrandingFlagWarnings.add(pendingKey);
    logBrandingFlagDiag("brandingFlagCheck", {
      flagKey,
      result: DEFAULT_BRAND_TEMPLATE_ID,
      reason: "cachePending",
      hint: "Flag not resolved yet; awaiting refreshBrandTemplateId().",
    });
  }

  refreshBrandTemplateId().catch(() => {});
  return DEFAULT_BRAND_TEMPLATE_ID;
}
